import { useReducer, useCallback } from 'react';
import type {
  ImageEditorState,
  ImageEditorAction,
  EditorTool,
  BrushSettings,
  TextSettings,
  ShapeSettings,
  MarqueeSettings,
  FilterSettings,
  CropState,
  Layer,
} from '../types';

const initialState: ImageEditorState = {
  activeTool: 'move',
  brushSettings: { color: '#000000', width: 4, opacity: 1 },
  textSettings: {
    fontFamily: 'Arial',
    fontSize: 48,
    fontWeight: 'normal',
    fontStyle: 'normal',
    underline: false,
    textAlign: 'left',
    fill: '#000000',
    arch: 0,
  },
  shapeSettings: {
    type: 'rect',
    fill: 'transparent',
    stroke: '#000000',
    strokeWidth: 2,
    cornerRadius: 0,
  },
  marqueeSettings: {
    shape: 'rect',
    forceSquare: false,
    color: '#0078d7',
    tolerance: 32,
    contiguous: true,
  },
  filterSettings: { brightness: 0, contrast: 0, saturation: 0 },
  layers: [],
  activeLayerId: null,
  layersPanelOpen: true,
  crop: { isActive: false, aspectRatio: null, rect: null },
  zoom: 1,
  canvasWidth: 0,
  canvasHeight: 0,
  selectedObjectIds: [],
  fileName: 'Untitled',
  fileId: null,
  isDirty: false,
  propertiesPanelOpen: true,
  cursorPosition: { x: 0, y: 0 },
  colorUnderCursor: '#000000',
};

function reducer(state: ImageEditorState, action: ImageEditorAction): ImageEditorState {
  switch (action.type) {
    case 'SET_ACTIVE_TOOL':
      return {
        ...state,
        activeTool: action.tool,
        // Deactivate crop when switching away
        crop: action.tool !== 'crop' ? { ...state.crop, isActive: false } : state.crop,
      };
    case 'UPDATE_BRUSH_SETTINGS':
      return { ...state, brushSettings: { ...state.brushSettings, ...action.settings } };
    case 'UPDATE_TEXT_SETTINGS':
      return { ...state, textSettings: { ...state.textSettings, ...action.settings } };
    case 'UPDATE_SHAPE_SETTINGS':
      return { ...state, shapeSettings: { ...state.shapeSettings, ...action.settings } };
    case 'UPDATE_MARQUEE_SETTINGS':
      return { ...state, marqueeSettings: { ...state.marqueeSettings, ...action.settings } };
    case 'UPDATE_FILTER_SETTINGS':
      return { ...state, filterSettings: { ...state.filterSettings, ...action.settings } };
    case 'SET_ZOOM':
      return { ...state, zoom: Math.max(0.1, Math.min(10, action.zoom)) };
    case 'SET_CANVAS_DIMENSIONS':
      return { ...state, canvasWidth: action.width, canvasHeight: action.height };
    case 'SET_CROP':
      return { ...state, crop: { ...state.crop, ...action.crop } };
    case 'SET_SELECTION':
      return { ...state, selectedObjectIds: action.objectIds };
    case 'SET_FILE_INFO':
      return { ...state, fileName: action.fileName, fileId: action.fileId };
    case 'MARK_DIRTY':
      return { ...state, isDirty: true };
    case 'MARK_CLEAN':
      return { ...state, isDirty: false };
    case 'TOGGLE_PROPERTIES_PANEL':
      return { ...state, propertiesPanelOpen: !state.propertiesPanelOpen };
    case 'TOGGLE_LAYERS_PANEL':
      return { ...state, layersPanelOpen: !state.layersPanelOpen };
    case 'SET_CURSOR_INFO':
      return {
        ...state,
        cursorPosition: { x: action.x, y: action.y },
        colorUnderCursor: action.color,
      };
    case 'SET_CURSOR_POSITION':
      return {
        ...state,
        cursorPosition: { x: action.x, y: action.y },
      };
    case 'ADD_LAYER':
      return {
        ...state,
        layers: [action.layer, ...state.layers],
        activeLayerId: action.setActive ? action.layer.id : state.activeLayerId,
      };
    case 'REMOVE_LAYER': {
      const remaining = state.layers.filter((l) => l.id !== action.id);
      return {
        ...state,
        layers: remaining,
        activeLayerId:
          state.activeLayerId === action.id ? (remaining[0]?.id ?? null) : state.activeLayerId,
      };
    }
    case 'RENAME_LAYER':
      return {
        ...state,
        layers: state.layers.map((l) => (l.id === action.id ? { ...l, name: action.name } : l)),
      };
    case 'TOGGLE_LAYER_VISIBILITY':
      return {
        ...state,
        layers: state.layers.map((l) => (l.id === action.id ? { ...l, visible: !l.visible } : l)),
      };
    case 'SET_ACTIVE_LAYER':
      return { ...state, activeLayerId: action.id };
    case 'REORDER_LAYER': {
      const { fromIndex, toIndex } = action;
      const len = state.layers.length;
      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= len ||
        toIndex >= len
      ) {
        return state;
      }
      const newLayers = [...state.layers];
      const [moved] = newLayers.splice(fromIndex, 1);
      newLayers.splice(toIndex, 0, moved);
      return { ...state, layers: newLayers };
    }
    case 'RESET_LAYERS':
      return { ...state, layers: action.layers, activeLayerId: action.activeLayerId };
    default:
      return state;
  }
}

export function useImageEditorState() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setActiveTool = useCallback(
    (tool: EditorTool) => dispatch({ type: 'SET_ACTIVE_TOOL', tool }),
    [],
  );
  const updateBrushSettings = useCallback(
    (settings: Partial<BrushSettings>) => dispatch({ type: 'UPDATE_BRUSH_SETTINGS', settings }),
    [],
  );
  const updateTextSettings = useCallback(
    (settings: Partial<TextSettings>) => dispatch({ type: 'UPDATE_TEXT_SETTINGS', settings }),
    [],
  );
  const updateShapeSettings = useCallback(
    (settings: Partial<ShapeSettings>) => dispatch({ type: 'UPDATE_SHAPE_SETTINGS', settings }),
    [],
  );
  const updateMarqueeSettings = useCallback(
    (settings: Partial<MarqueeSettings>) => dispatch({ type: 'UPDATE_MARQUEE_SETTINGS', settings }),
    [],
  );
  const updateFilterSettings = useCallback(
    (settings: Partial<FilterSettings>) => dispatch({ type: 'UPDATE_FILTER_SETTINGS', settings }),
    [],
  );
  const setZoom = useCallback((zoom: number) => dispatch({ type: 'SET_ZOOM', zoom }), []);
  const setCanvasDimensions = useCallback(
    (width: number, height: number) => dispatch({ type: 'SET_CANVAS_DIMENSIONS', width, height }),
    [],
  );
  const setCrop = useCallback(
    (crop: Partial<CropState>) => dispatch({ type: 'SET_CROP', crop }),
    [],
  );
  const setSelection = useCallback(
    (objectIds: string[]) => dispatch({ type: 'SET_SELECTION', objectIds }),
    [],
  );
  const setFileInfo = useCallback(
    (fileName: string, fileId: string | null) =>
      dispatch({ type: 'SET_FILE_INFO', fileName, fileId }),
    [],
  );
  const markDirty = useCallback(() => dispatch({ type: 'MARK_DIRTY' }), []);
  const markClean = useCallback(() => dispatch({ type: 'MARK_CLEAN' }), []);
  const togglePropertiesPanel = useCallback(
    () => dispatch({ type: 'TOGGLE_PROPERTIES_PANEL' }),
    [],
  );
  const toggleLayersPanel = useCallback(() => dispatch({ type: 'TOGGLE_LAYERS_PANEL' }), []);
  const setCursorInfo = useCallback(
    (x: number, y: number, color: string) => dispatch({ type: 'SET_CURSOR_INFO', x, y, color }),
    [],
  );
  const setCursorPosition = useCallback(
    (x: number, y: number) => dispatch({ type: 'SET_CURSOR_POSITION', x, y }),
    [],
  );
  const addLayer = useCallback(
    (layer: Layer, setActive = true) => dispatch({ type: 'ADD_LAYER', layer, setActive }),
    [],
  );
  const removeLayer = useCallback((id: string) => dispatch({ type: 'REMOVE_LAYER', id }), []);
  const renameLayer = useCallback(
    (id: string, name: string) => dispatch({ type: 'RENAME_LAYER', id, name }),
    [],
  );
  const toggleLayerVisibility = useCallback(
    (id: string) => dispatch({ type: 'TOGGLE_LAYER_VISIBILITY', id }),
    [],
  );
  const setActiveLayer = useCallback(
    (id: string | null) => dispatch({ type: 'SET_ACTIVE_LAYER', id }),
    [],
  );
  const reorderLayer = useCallback(
    (fromIndex: number, toIndex: number) => dispatch({ type: 'REORDER_LAYER', fromIndex, toIndex }),
    [],
  );
  const resetLayers = useCallback(
    (layers: Layer[], activeLayerId: string | null = layers[0]?.id ?? null) =>
      dispatch({ type: 'RESET_LAYERS', layers, activeLayerId }),
    [],
  );

  return {
    state,
    dispatch,
    setActiveTool,
    updateBrushSettings,
    updateTextSettings,
    updateShapeSettings,
    updateMarqueeSettings,
    updateFilterSettings,
    setZoom,
    setCanvasDimensions,
    setCrop,
    setSelection,
    setFileInfo,
    markDirty,
    markClean,
    togglePropertiesPanel,
    toggleLayersPanel,
    setCursorInfo,
    setCursorPosition,
    addLayer,
    removeLayer,
    renameLayer,
    toggleLayerVisibility,
    setActiveLayer,
    reorderLayer,
    resetLayers,
  };
}

export { initialState, reducer };
